import React, { useMemo, useCallback, useEffect, useState, UIEvent, Suspense, Fragment, useRef, useLayoutEffect } from 'react';
import { basedAxios } from '../../store/Store';
import { runInAction } from 'mobx';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from '@material-ui/core';
import json2mq from 'json2mq';
import MobileMenu from './MobileMenu';
import DesktopMenu from './DesktopMenu';

function hasMultipleRows(container) {
  const items = container.children;
  if (items.length < 2) return false; // Needs at least two items to check wrapping

  const firstTop = items[0].getBoundingClientRect().top;
  const lastTop = items[items.length - 1].getBoundingClientRect().top;

  return firstTop !== lastTop; // If they are different, wrapping occurred
}

export default function Menu(props: any) {
  const [layouts, setLayouts] = useState<any>([]);
  const [menuItems, setMenuItems] = useState<any>([]);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const navigator = useNavigate();
  const menuItemsRef = useRef<any>(null);

  const isSmartphone = useMediaQuery(
    json2mq({
      maxWidth: 768,
    })
  );

  const { cssId, settings, environment, current, configureValidation, posY, setRenderSticky, renderSticky, width: moduleWidth, contentLayout } = props; // TODO: the module should know what device it is on, not the parent

  const {
    fixedOnScroll,
    menuGroup = '',
    showLogo = false,
    logoSource = '',
    menuLayout = 'L1:M*',
    mobileMenuPos = 'LEFT',
    logoLink,
    disableMenuItemsOverflow = false,
  } = settings;

  function handleMenuOverflow() {
    if (menuItemsRef.current) {
      const items = menuItemsRef.current.children;
      if (items.length < 2) {
        setShowMobileMenu(false);
      } // Needs at least two items to check wrapping

      const firstTop = items[0].getBoundingClientRect().top;
      const lastTop = items[items.length - 1].getBoundingClientRect().top;
      setShowMobileMenu(firstTop !== lastTop); // If they are different, wrapping occurred
    }
  }

  useLayoutEffect(() => {
    if (disableMenuItemsOverflow && menuItemsRef?.current) {
      handleMenuOverflow();
      window.addEventListener('resize', handleMenuOverflow);
    }

    return () => {
      window.removeEventListener('resize', handleMenuOverflow);
    };
  }, [disableMenuItemsOverflow, menuItemsRef?.current]);

  const isInViewport = useCallback(
    (event: UIEvent<HTMLElement>) => {
      const scrollYPos = (event.target as HTMLElement).scrollTop;
      if (scrollYPos > posY) {
        if (!renderSticky) setRenderSticky(true);
      } else {
        if (renderSticky) setRenderSticky(false);
      }
    },
    [posY, renderSticky]
  );

  useEffect(() => {
    const layout = (document.querySelector('.body') as HTMLElement) || null; // Cast the result to HTMLElement
    if (layout && fixedOnScroll) {
      layout.addEventListener('scroll', isInViewport as unknown as EventListener);
    }
    return () => {
      if (layout && fixedOnScroll) {
        layout.removeEventListener('scroll', isInViewport as unknown as EventListener);
      }
    };
  }, [fixedOnScroll, isInViewport]);

  const fetchLayouts = useCallback(
    async (menuItem: any) => {
      try {
        // This code previously fetched all layouts on the event when the menu was fetched. This is not needed anymore since the layouts are fetched when the menu item is clicked.
        // const response = await basedAxios.get(`./cms/getall?eventId=${environment.eventId}`);
        // setLayouts(response.data);
        // const firstLayoutCode = menuItem?.[0]?.layoutCode;
        // if (firstLayoutCode && current.hasContentView) {
        //   const layout = response.data?.find((item: any) => item.layoutCode === firstLayoutCode);
        //   if (layout) {
        //     runInAction(() => {
        //       current.scopedLayout = layout;
        //     });
        //   }
        // }
      } catch (error) {
        console.error(error);
      }
    },
    [current.hasContentView, environment.eventId]
  );

  const replaceUrlTags = useCallback(
    (url: string) => {
      const urlString = window.location.href;

      function getQueryParam(url: string, name: string) {
        name = name.replace(/[[]]/g, '\\$&');
        const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)', 'i'); // 'i' flag for case-insensitivity
        const results = regex.exec(url);

        if (!results) return null;
        if (!results[2]) return '';

        return decodeURIComponent(results[2].replace(/\+/g, ' '));
      }

      let path = url.toString();
      const badgeNo = getQueryParam(urlString, 'badgeno') ?? getQueryParam(urlString, 'clientkey') ?? '';
      const lang = getQueryParam(urlString, 'lang') ?? '';

      if (/\[BadgeNo\]/gi.test(path)) {
        if (badgeNo) {
          path = path.replace(/\[BadgeNo\]/gi, badgeNo);
        } else {
          path = path.replace(/&*badgeno=\[BadgeNo\]/gi, '');
          path = path.replace(/&*clientkey=\[BadgeNo\]/gi, '');
        }
      }

      if (/\[ClientKey\]/gi.test(path)) {
        if (badgeNo) {
          path = path.replace(/\[ClientKey\]/gi, badgeNo);
        } else {
          path = path.replace(/&*badgeno=\[ClientKey\]/gi, '');
          path = path.replace(/&*clientkey=\[ClientKey\]/gi, '');
        }
      }

      if (/lang=.{3}/gi.test(path) && lang) {
        path = path.replace(/lang=.{3}/gi, 'lang=' + lang);
      }

      return path;
    },
    [environment.eventId]
  );

  useEffect(() => {
    const hash = window.location.hash;

    if (hash) {
      const section = document.getElementById(hash.substring(1));

      if (section) {
        section.scrollIntoView({
          block: 'start',
          behavior: 'smooth',
        });
      }
    }
  }, []);

  const fetchMenuItems = useCallback(async () => {
    try {
      const response = await basedAxios.get(`./menu/${environment.eventId}`);
      const newMenuItems = Array.from(response.data);

      if (newMenuItems && Array.isArray(newMenuItems)) {
        const filteredMenuItems = newMenuItems.filter((menuItem: any) => menuItem.languageCode === environment.lang && menuItem.menuGroupName === menuGroup);
        const sortedMenuItems = filteredMenuItems.sort((a: any, b: any) => a.sortOrder - b.sortOrder);
        const parsedMenuItems = sortedMenuItems.map((menuItem: any) => ({
          ...menuItem,
        }));

        setMenuItems(parsedMenuItems);

        if (newMenuItems.filter((newMenuItem: any) => newMenuItem.layoutCode != null).length > 0) {
          fetchLayouts(newMenuItems);
        }
      }
    } catch (error) {
      console.error(error);
    }
  }, [environment.eventId, fetchLayouts, replaceUrlTags, setMenuItems, environment, menuGroup]);

  const navigate = useCallback(
    (url: string, target: string) => {
      if (current.hasContentView) {
        const menuItem = menuItems.find((item: any) => item.url == url);

        const layout = layouts.find(l => l.layoutCode === menuItem?.layoutCode);
        if (layout) {
          configureValidation();
          //Remove all mobX refs
          return runInAction(() => {
            current.scopedLayout = layout;
          });
        }
      }

      let urlPathName = url.split('?')?.[0];
      const hash = url?.split('#')?.[1];

      if (!urlPathName.includes('localhost') && hash) {
      }

      if (hash && (urlPathName === window.location.pathname || urlPathName === window.location.pathname.replace('/cview', ''))) {
        const section = document.getElementById(hash);

        window.location.hash = hash;

        if (section) {
          section.scrollIntoView({
            inline: 'nearest',
            block: 'end',
            behavior: 'smooth',
          });
          return;
        }
      }

      if (url.includes('jirango') || url.includes('localhost') || url.startsWith('/')) {
        if (/lang=[a-z]{3}/i.test(url)) {
          const langString = /[\?\&]{1}lang=[a-z]{3}/i.exec(url);
          if (langString) {
            url.replace(langString.toString(), `lang=${environment.lang}`);
          }
        } else if (url.includes('?')) {
          url += `&lang=${environment.lang}`;
        } else {
          url += `?lang=${environment.lang}`;
        }

        if (url.includes('#')) {
          url = url.replace('#' + hash, '');
          url += `#${hash}`;
        }
      }

      if (url?.startsWith('www.')) {
        url = 'http://' + url;
      }

      const convertedUrl = replaceUrlTags(url);

      if (target == '_self' && !url.includes('http')) {
        navigator(convertedUrl);
      } else {
        window.open(convertedUrl, target);
      }
    },
    [replaceUrlTags, current, configureValidation, environment.lang, layouts, menuItems, navigator]
  );

  const menuLayoutParts = useMemo(() => menuLayout.split(':').filter(part => part != ''), [menuLayout]);

  useEffect(() => {
    fetchMenuItems();
  }, []);

  if (menuItems.length === 0) {
    return null;
  }

  return (
    <>
      {(isSmartphone || showMobileMenu) && (
        <header className="cms-menu mobile-menu">
          <MobileMenu
            logoLink={logoLink}
            mobileMenuPos={mobileMenuPos}
            menuItems={menuItems}
            contentLayout={contentLayout}
            scopedLayout={current.scopedLayout}
            cssId={cssId}
            showLogo={showLogo}
            logoSrc={logoSource}
            navigate={navigate}
          />
        </header>
      )}
      {!isSmartphone && (
        <div className={showMobileMenu ? '-translate-y-[5000px] absolute -z-10' : 'translate-y-0'} style={{ display: 'inherit', width: '100%' }}>
          <DesktopMenu
            menuLayoutParts={menuLayoutParts}
            moduleWidth={moduleWidth}
            logoLink={logoLink}
            mobileMenuPos={mobileMenuPos}
            menuItems={menuItems}
            contentLayout={contentLayout}
            scopedLayout={current.scopedLayout}
            cssId={cssId}
            menuItemsRef={menuItemsRef}
            current={current}
            showLogo={showLogo}
            logoSrc={logoSource}
            navigate={navigate}
            settings={settings}
          />
        </div>
      )}
    </>
  );
}
